import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, FileText, Search } from "lucide-react";
import AdminLayout from "../../layouts/AdminLayout";
import { apiRequest } from "../../utils/apiRequest";

const getDocumentStatus = (expiryDate) => {
  if (!expiryDate) return "No Expiry";

  const today = new Date();
  const expiry = new Date(expiryDate);

  today.setHours(0, 0, 0, 0);
  expiry.setHours(0, 0, 0, 0);

  const diffInMs = expiry - today;
  const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays < 0) return "Expired";
  if (diffInDays <= 30) return "Expiring Soon";
  return "Valid";
};

const formatDate = (dateString) => {
  if (!dateString) return "—";

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const statusClasses = {
  Valid: "bg-emerald-100 text-emerald-700",
  "Expiring Soon": "bg-amber-100 text-amber-700",
  Expired: "bg-rose-100 text-rose-700",
  "No Expiry": "bg-slate-100 text-slate-700",
};

export default function DocumentsPage() {
  const token = localStorage.getItem("staff_portal_token");

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [documentTypeFilter, setDocumentTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    setError("");

    const res = await apiRequest({
      script: "admin-get-documents",
      token,
      data: {},
    });

    if (!res?.success) {
      setError(res?.error || "Failed to fetch documents");
      setLoading(false);
      return;
    }

    const mappedDocuments = (res.documents || []).map((doc) => ({
      ...doc,
      status: getDocumentStatus(doc.expiry_date),
    }));

    setDocuments(mappedDocuments);
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const filteredDocuments = useMemo(() => {
    const query = search.trim().toLowerCase();

    return documents.filter((doc) => {
      const matchesSearch =
        !query ||
        String(doc.staff_name || "")
          .toLowerCase()
          .includes(query) ||
        String(doc.staff_id || "")
          .toLowerCase()
          .includes(query) ||
        String(doc.file_name || "")
          .toLowerCase()
          .includes(query);

      const matchesType =
        !documentTypeFilter || doc.document_type === documentTypeFilter;

      const matchesStatus = !statusFilter || doc.status === statusFilter;

      return matchesSearch && matchesType && matchesStatus;
    });
  }, [documents, search, documentTypeFilter, statusFilter]);

  const stats = useMemo(() => {
    return {
      total: documents.length,
      expiringSoon: documents.filter(
        (doc) => doc.status === "Expiring Soon",
      ).length,
      expired: documents.filter((doc) => doc.status === "Expired").length,
    };
  }, [documents]);

  const documentTypes = useMemo(() => {
    return [...new Set(documents.map((doc) => doc.document_type).filter(Boolean))];
  }, [documents]);

  const handleViewDocument = (filePath) => {
    if (!filePath) return;
    window.open(`https://staff.lasgidirestaurant.com/${filePath}`, "_blank");
  };

  return (
    <AdminLayout title="Documents">
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Documents</p>
                <h3 className="mt-2 text-3xl font-bold text-slate-900">
                  {stats.total}
                </h3>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100">
                <FileText size={22} className="text-slate-700" />
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Expiring Soon</p>
                <h3 className="mt-2 text-3xl font-bold text-slate-900">
                  {stats.expiringSoon}
                </h3>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-100">
                <AlertTriangle size={22} className="text-amber-700" />
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Expired</p>
                <h3 className="mt-2 text-3xl font-bold text-slate-900">
                  {stats.expired}
                </h3>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-100">
                <AlertTriangle size={22} className="text-rose-700" />
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex w-full flex-col gap-4 md:flex-row md:items-center">
              <div className="relative w-full md:max-w-sm">
                <Search
                  size={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by staff name, ID or file name..."
                  className="w-full rounded-xl border border-slate-300 py-3 pl-10 pr-4 text-sm outline-none focus:ring-2 focus:ring-slate-900"
                />
              </div>

              <select
                value={documentTypeFilter}
                onChange={(e) => setDocumentTypeFilter(e.target.value)}
                className="rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-slate-900"
              >
                <option value="">All Document Types</option>
                {documentTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-slate-900"
              >
                <option value="">All Statuses</option>
                <option value="Valid">Valid</option>
                <option value="Expiring Soon">Expiring Soon</option>
                <option value="Expired">Expired</option>
                <option value="No Expiry">No Expiry</option>
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[950px]">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr className="text-left">
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Staff
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Document Type
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Expiry Date
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Status
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="6"
                      className="px-6 py-10 text-center text-sm text-slate-500"
                    >
                      Loading documents...
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td
                      colSpan="6"
                      className="px-6 py-10 text-center text-sm text-red-600"
                    >
                      {error}
                    </td>
                  </tr>
                ) : filteredDocuments.length === 0 ? (
                  <tr>
                    <td
                      colSpan="6"
                      className="px-6 py-10 text-center text-sm text-slate-500"
                    >
                      No documents found.
                    </td>
                  </tr>
                ) : (
                  filteredDocuments.map((doc) => (
                    <tr
                      key={doc.id}
                      className="border-b border-slate-100 last:border-b-0"
                    >
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-sm font-medium text-slate-900">
                            {doc.staff_name}
                          </p>
                          <p className="text-xs text-slate-500">{doc.staff_id}</p>
                        </div>
                      </td>

                      <td className="px-6 py-4 text-sm text-slate-700">
                        {doc.document_type || "—"}
                      </td>


                      <td className="px-6 py-4 text-sm text-slate-700">
                        {formatDate(doc.expiry_date)}
                      </td>

                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${
                            statusClasses[doc.status]
                          }`}
                        >
                          {doc.status}
                        </span>
                      </td>

                      <td className="px-6 py-4">
                        <button
                          type="button"
                          onClick={() => handleViewDocument(doc.file_path)}
                          disabled={!doc.file_path}
                          className={`text-sm font-medium ${
                            doc.file_path
                              ? "text-slate-900 hover:underline"
                              : "cursor-not-allowed text-slate-400"
                          }`}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}